// apps/web/src/components/miner/StateStrip.tsx
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  readonly isHealthy: boolean | null;
  readonly isJailed: boolean | null;
  readonly missedPercent: number | null;
}

export function StateStrip({ isHealthy, isJailed, missedPercent }: Props): ReactNode {
  const { t } = useTranslation();

  const uptime = missedPercent != null ? 100 - missedPercent : null;
  const uptimeClass = uptime == null
    ? 'text-text-primary'
    : uptime >= 99
      ? 'text-pos'
      : uptime >= 95
        ? 'text-text-primary'
        : 'text-warning';

  return (
    <div className="mb-3.5 grid grid-cols-3 overflow-hidden rounded-2xl bg-surface-2">
      <Cell
        label={t('minerDetail.nodeStatus')}
        value={
          isHealthy === true
            ? <span className="inline-flex items-center gap-1.5 text-pos"><Pulse />{t('minerDetail.healthy')}</span>
            : isHealthy === false
              ? <span className="inline-flex items-center gap-1.5 text-neg"><Pulse />{t('minerDetail.unhealthy')}</span>
              : <span className="text-text-primary">—</span>
        }
      />
      <Cell
        label={t('minerDetail.jail')}
        value={
          isJailed === false
            ? <span className="text-pos">{t('minerDetail.jailFree')}</span>
            : isJailed === true
              ? <span className="inline-flex items-center gap-1.5 text-neg"><Pulse />{t('minerDetail.jail')}</span>
              : <span className="text-text-primary">—</span>
        }
      />
      <Cell
        label={t('miner.uptime')}
        value={
          uptime == null
            ? <span className="text-text-primary">—</span>
            : <span className={uptimeClass}>{uptime.toFixed(1)}%</span>
        }
        last
      />
    </div>
  );
}

function Cell({ label, value, last = false }: { label: string; value: ReactNode; last?: boolean }): ReactNode {
  return (
    <div className={`flex flex-col items-start gap-1 px-3.5 py-3 ${last ? '' : 'border-r border-border-hairline'}`}>
      <div className="text-[9px] font-semibold uppercase tracking-[1.2px] text-text-tertiary">
        {label}
      </div>
      <div className="text-[13px] font-semibold">{value}</div>
    </div>
  );
}

function Pulse(): ReactNode {
  return (
    <span
      className="inline-block h-1.5 w-1.5 rounded-full bg-current"
      aria-hidden
    />
  );
}
